import { useId, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useOptionalCurrency, useOptionalLocale, useLocale } from "../../../intl";
import { localeToUrlSegment } from "../../../intl/manifestI18n";
import { writeLocaleToStorage } from "../../../intl/localeResolution.js";
import { writeDisplayCurrencyToStorage } from "../../../intl/currencyResolution.js";
import { useOptionalClientView } from "../../../clientViewRuntime/context/ClientViewProvider";
import CasaMapaSelect from "./CasaMapaSelect";

const LOCALE_LABELS = {
  "es-CO": "ES",
  "en-US": "EN",
  "ru-RU": "RU",
};

/**
 * Locale + currency tools consuming existing providers (URL-first when route locale available).
 * Pass explicit props in fixtures; hooks fill gaps when providers are mounted.
 */
export default function CasaMapaLocaleCurrencyTools({
  locale: localeProp,
  currency: currencyProp,
  supportedLocales: localesProp,
  supportedCurrencies: currenciesProp,
  onLocaleChange,
  onCurrencyChange,
  buildLocaleSwitchPath = null,
  compact = false,
  menu = false,
  className = "",
}) {
  const idBase = useId();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useLocale();
  const localeCtx = useOptionalLocale();
  const currencyCtx = useOptionalCurrency();
  useOptionalClientView();

  const locale = localeProp || localeCtx?.locale || "es-CO";
  const currency = currencyProp || currencyCtx?.displayCurrency || "COP";
  const supportedLocales =
    localesProp || localeCtx?.supportedLocales || ["es-CO", "en-US", "ru-RU"];
  const supportedCurrencies =
    currenciesProp || currencyCtx?.supportedCurrencies || ["COP", "USD", "EUR", "RUB"];

  const localeOptions = useMemo(
    () =>
      (supportedLocales.length ? supportedLocales : ["es-CO", "en-US", "ru-RU"]).map((value) => ({
        value,
        label: LOCALE_LABELS[value] || localeToUrlSegment(value)?.toUpperCase() || value,
      })),
    [supportedLocales]
  );

  const currencyOptions = useMemo(
    () =>
      (supportedCurrencies.length ? supportedCurrencies : ["COP"]).map((value) => ({
        value,
        label: value,
      })),
    [supportedCurrencies]
  );

  const handleLocaleChange = (nextLocale) => {
    writeLocaleToStorage(nextLocale);
    if (onLocaleChange) {
      onLocaleChange(nextLocale);
      return;
    }
    if (typeof buildLocaleSwitchPath === "function") {
      const nextPath = buildLocaleSwitchPath(nextLocale);
      if (nextPath) {
        const currentPath = `${location.pathname}${location.search}`;
        if (nextPath !== currentPath) {
          navigate(nextPath);
        }
        return;
      }
    }
    localeCtx?.setLocale?.(nextLocale);
  };

  const handleCurrencyChange = (nextCurrency) => {
    writeDisplayCurrencyToStorage(nextCurrency);
    if (onCurrencyChange) {
      onCurrencyChange(nextCurrency);
      return;
    }
    currencyCtx?.setDisplayCurrency?.(nextCurrency);
  };

  return (
    <div
      className={[
        "cm-chrome-tools",
        compact ? "cm-chrome-tools--compact" : "",
        menu ? "cm-chrome-tools--menu" : "",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {localeOptions.length > 1 ? (
        <CasaMapaSelect
          id={`${idBase}-lang`}
          label={t("realEstate.casaMapa.chrome.language")}
          hideLabelVisually={!menu}
          compact={compact && !menu}
          value={locale}
          options={localeOptions}
          onChange={handleLocaleChange}
        />
      ) : null}
      {currencyOptions.length > 1 ? (
        <CasaMapaSelect
          id={`${idBase}-currency`}
          label={t("realEstate.casaMapa.chrome.currency")}
          hideLabelVisually={!menu}
          compact={compact && !menu}
          value={currency}
          options={currencyOptions}
          onChange={handleCurrencyChange}
        />
      ) : null}
    </div>
  );
}
